import { onMounted } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { STEP_TITLE, trackCancellationStepDone, trackCancellationStepShow, type StepTitle } from '@/analytics/events'

// Navigation direction via Vue Router's `history.state.position` (a monotonic index it keeps
// per entry). On a browser/Back-button pop the position is already the target's when guards
// run; on a forward push it isn't yet — so comparing against the last-settled position tells
// us the direction without any popstate bookkeeping.
function positionNow(): number {
	return Number(window.history.state?.position ?? 0)
}

// One global `afterEach` keeps the last-settled position. It is installed lazily from the first
// composable call via `useRouter()` — NOT by importing the `@/router` singleton at module load.
// With eager routes that import is circular (router → view → @/analytics → here → @/router) and
// the top-level `router.afterEach` would run before `router` is initialized (TDZ), blanking the
// app. `useRouter()` returns the same instance and is only called inside setup. See ADR 0011.
let lastPosition = positionNow()
let lastNavBackwards = false
let afterEachInstalled = false

export interface CancelStepOptions {
	/** Read the step's current responses (analytics taxonomy) — used to seed on (re)entry. */
	getResponses?: () => string[]
	/** Read the step's current free-text — used to seed on (re)entry. */
	getText?: () => string
	/**
	 * The commitment amounts the screen shows (AL-15098, CR 73 / 74 — `data.commitment` on WHEN_TO_CANCEL
	 * and the two summaries). Read once on mount: Done repeats what Show reported, though the cancel
	 * call may have changed the subscription by the time the screen is left.
	 */
	getCommitment?: () => object | undefined
	/** More keys for `data`, on Show and Done alike, read once on mount (`payment_error` on PAYMENT_UNSUCCESSFUL). */
	getData?: () => Record<string, unknown> | undefined
}

/**
 * Fires CancellationFlowStepShow on mount and CancellationFlowStepDone on leave.
 *
 * Responses/text are read from a snapshot the view updates via `record()` at change-time —
 * NOT from the live store at leave-time — because Confirm.onSubmit clears the survey store
 * before navigating, which would otherwise make Done report empty data.
 *
 * `done(responses)` sends Done right away (once — the leave then sends nothing): the summaries close
 * their step on the tap, before the "Processing your payment…" overlay opens its own (CR 74).
 */
export function useCancelStepLogging(title: StepTitle, options: CancelStepOptions = {}) {
	const router = useRouter()
	if (!afterEachInstalled) {
		afterEachInstalled = true
		router.afterEach(() => {
			const pos = positionNow()
			lastNavBackwards = pos < lastPosition
			lastPosition = pos
		})
	}

	let arrivalBackwards = false
	let extra: Record<string, unknown> = {}
	let doneSent = false
	const captured: { responses: string[]; text: string } = { responses: [], text: '' }

	function record(responses: string[] = [], text = ''): void {
		captured.responses = responses
		captured.text = text
	}

	function sendDone(isStepBack: boolean): void {
		if (doneSent) return
		doneSent = true
		trackCancellationStepDone(
			title,
			{ responses: captured.responses, text: captured.text, ...extra },
			arrivalBackwards,
			isStepBack,
		)
	}

	function done(responses?: string[]): void {
		if (responses) record(responses, captured.text)
		sendDone(false)
	}

	onMounted(() => {
		arrivalBackwards = lastNavBackwards
		const commitment = options.getCommitment?.()
		extra = { ...(commitment ? { commitment } : {}), ...(options.getData?.() ?? {}) }
		trackCancellationStepShow(title, arrivalBackwards, Object.keys(extra).length ? extra : undefined)
		if (options.getResponses) captured.responses = options.getResponses()
		if (options.getText) captured.text = options.getText()
	})

	onBeforeRouteLeave(() => {
		sendDone(positionNow() < lastPosition)
	})

	return { title, record, done }
}

/**
 * A step that is an overlay, not a route — no mount/leave to hang Show/Done on: Show now, and the returned
 * closer sends its Done once, with the answer that closed it (none when it closed by itself). `data` goes
 * on both (`step` on CANCELLATION_FAILED).
 */
export function overlayStep(title: StepTitle, data?: Record<string, unknown>): (responses?: string[]) => void {
	trackCancellationStepShow(title, false, data)
	let closed = false
	return (responses = []) => {
		if (closed) return
		closed = true
		trackCancellationStepDone(title, { responses, text: '', ...data }, false, false)
	}
}

/**
 * "Processing your payment…" (PROCESSING_PAYMENT, CR 73 / 74): Show when it opens, Done when the call
 * returns — on success and on failure alike.
 */
export function processingPaymentStep(): () => void {
	const close = overlayStep(STEP_TITLE.PROCESSING_PAYMENT)
	return () => close()
}
